import { useForm } from 'react-hook-form'
// @ts-nocheck
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { resendCode, verifyEmail } from '../api/auth'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAuthEmail } from '@shared/libs/authStorage'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@shared/toast/ToastProvider'
import { FormField } from '@shared/ui/FormField'
import { Input } from '@shared/ui/Input'
import { Button } from '@shared/ui/Button'
import { CodeInput6 } from '@shared/ui/CodeInput6'
import { http } from '@shared/libs/http'

const schema = z.object({ email: z.string().email(), code: z.string().length(6) })
type FormValues = z.infer<typeof schema>

export function VerifyEmailPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const { push } = useToast()
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState<number>(0)
  const defaultEmail = getAuthEmail() ?? ''
  const { register, handleSubmit, formState, getValues, setValue } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { email: defaultEmail } })

  // Prime CSRF cookie for POSTs
  useEffect(() => {
    http.get('/auth/session').catch(() => {})
  }, [])

  const onSubmit = async (v: FormValues) => {
    setErr(null)
    try {
      await verifyEmail(v)
      setMsg('Email verified. Redirecting…')
      push({ type: 'success', title: 'Email verified' })
      await qc.invalidateQueries({ queryKey: ['session'] })
      setTimeout(() => nav('/'), 300)
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? 'Verification failed')
    }
  }

  const onResend = async () => {
    setMsg(null)
    setErr(null)
    try {
      const email = getValues('email') || defaultEmail
      if (!email) throw new Error('Enter your email first')
      await resendCode({ purpose: 'verify_email', email })
      setMsg('Code resent')
      setCooldown(30)
      const t = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) {
            clearInterval(t)
            return 0
          }
          return c - 1
        })
      }, 1000)
    } catch (e: any) {
      const m = e?.response?.data?.detail ?? 'Could not resend'
      setErr(m)
      push({ type: 'error', title: 'Resend failed', description: m })
    }
  }

  return (
    <div className="min-h-screen grid place-items-center">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-6">
        <h1 className="text-xl font-semibold mb-4">Verify your email</h1>
        {msg && <div className="mb-2 text-sm text-success">{msg}</div>}
        {err && <div className="mb-2 text-sm text-danger">{err}</div>}
        <form className="grid gap-3" noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Email" message={formState.errors.email?.message}>
            <Input type="email" {...register('email')} onChange={(e) => setValue('email', e.target.value)} />
          </FormField>
          <FormField label="Code" message={formState.errors.code?.message}>
            <CodeInput6 {...register('code')} />
          </FormField>
          <div className="flex gap-2">
            <Button disabled={formState.isSubmitting}>Verify</Button>
            <Button type="button" variant="outline" onClick={onResend} disabled={cooldown>0}>
              {cooldown>0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
 
// @ts-nocheck
